import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { parseIsoDate } from "@/lib/financial/calc";
import type { FinancialAccount, FinancialCategory, PaymentMethod } from "@/lib/financial/types";

/** Formatação de moeda, datas e rótulos do módulo, sensível ao idioma. */
export function useFinancialFormat() {
  const { t, i18n } = useTranslation("financial");
  const locale = i18n.language?.startsWith("en") ? "en-US" : "pt-BR";

  return useMemo(() => {
    const n2 = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const n0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const pctFmt = new Intl.NumberFormat(locale, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const monthShortFmt = new Intl.DateTimeFormat(locale, { month: "short" });
    const monthLongFmt = new Intl.DateTimeFormat(locale, { month: "long" });
    const numFmt = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
    const longFmt = new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const dayMonthFmt = new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "long" });
    const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });

    const cleanMonth = (s: string) => s.replace(".", "");
    const monthShort = (m: number) => cleanMonth(monthShortFmt.format(new Date(2026, m, 15)));
    const monthLong = (m: number) => monthLongFmt.format(new Date(2026, m, 15));
    const sign = (v: number) => (v < 0 ? "− " : "");

    return {
      locale,
      brl: (v: number) => `${sign(v)}R$ ${n2.format(Math.abs(v))}`,
      brl0: (v: number) => `${sign(v)}R$ ${n0.format(Math.abs(Math.round(v)))}`,
      signed: (v: number) => `${v < 0 ? "− " : "+ "}R$ ${n2.format(Math.abs(v))}`,
      /** número compacto de tabela (sem R$) */
      num: (v: number) => `${v < 0 ? "−" : ""}${n0.format(Math.abs(Math.round(v)))}`,
      moneyInput: (v: number) => n2.format(Math.abs(v)),
      pct: (v: number) => (v < 0 ? "−" : "") + pctFmt.format(Math.abs(v)),
      monthShort,
      monthLong,
      dnum: (iso: string) => numFmt.format(parseIsoDate(iso)),
      dshort: (iso: string) => {
        const d = parseIsoDate(iso);
        return `${d.getDate()} ${monthShort(d.getMonth())}`;
      },
      dlong: (iso: string) => {
        const s = longFmt.format(parseIsoDate(iso));
        return s.charAt(0).toUpperCase() + s.slice(1);
      },
      dayMonth: (iso: string) => dayMonthFmt.format(parseIsoDate(iso)),
      time: (ts: string) => timeFmt.format(new Date(ts)),
      dow: (iso: string) => (t("dow.short", { returnObjects: true }) as string[])[parseIsoDate(iso).getDay()],
      method: (m: PaymentMethod | null | undefined) => (m ? t(`methods.${m}`) : "—"),
      category: (c: FinancialCategory | undefined | null) =>
        !c ? t("categories.none") : c.system_key ? t(`categories.${c.system_key}`) : c.name,
      account: (a: FinancialAccount | undefined | null) =>
        !a ? "—" : a.system_key ? t(`accounts.${a.system_key}`) : a.name,
    };
  }, [locale, t]);
}

export type FinancialFormat = ReturnType<typeof useFinancialFormat>;
